import { useCallback, useEffect, useState } from "react";
import { DndContext, PointerSensor, useSensor, useSensors, closestCorners, DragOverlay } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { ChevronLeft, X } from "lucide-react";
import api from "@/lib/api";
import { PIPELINE } from "@/constants/testIds/crm";
import { getStatusLabel, getStatusToneClasses } from "@/lib/statusStyles";

const COLUMNS = [
    { id: "new_lead", label: "New Lead", accent: "border-t-muted-foreground" },
    { id: "contacted", label: "Contacted", accent: "border-t-primary" },
    { id: "follow_up", label: "Follow-up", accent: "border-t-warning" },
    { id: "proposal_sent", label: "Proposal Sent", accent: "border-t-chart-4" },
    { id: "negotiation", label: "Negotiation", accent: "border-t-chart-3" },
    { id: "won", label: "Won", accent: "border-t-success" },
    { id: "lost", label: "Lost", accent: "border-t-destructive" },
];

const PRIORITY_BAR = {
    urgent: "priority-bar-urgent",
    high: "priority-bar-high",
    medium: "priority-bar-medium",
    low: "priority-bar-low",
};

function LeadCard({ lead, dragging, onClick }) {
    return (
        <div
            data-testid={PIPELINE.card}
            onClick={onClick}
            className={`kanban-card ${PRIORITY_BAR[lead.priority] || ""} border rounded-md p-3 space-y-2 ${dragging ? "shadow-lg" : ""} ${onClick ? "cursor-pointer" : ""} ${getStatusToneClasses(lead.status, { group: "status", variant: "surface" })}`}
        >
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <div className="font-medium text-sm truncate">{lead.company}</div>
                    <div className="text-[11px] text-muted-foreground truncate">{lead.client_name}</div>
                </div>
                <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded border shrink-0 ${getStatusToneClasses(lead.priority, { group: "priority", variant: "badge" })}`}>{lead.priority}</span>
            </div>
            <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                <span className="font-mono">${(lead.value || 0).toLocaleString()}</span>
                <span className="uppercase tracking-wider">{lead.source}</span>
            </div>
            <div className="flex justify-end">
                <span className={`uppercase tracking-wider px-2 py-0.5 rounded border text-[10px] font-semibold ${getStatusToneClasses(lead.status, { group: "status", variant: "badge" })}`}>{getStatusLabel(lead.status)}</span>
            </div>
        </div>
    );
}

function SortableCard({ lead, onOpen }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: lead.id, data: { lead } });
    const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };
    return (
        <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
            <LeadCard lead={lead} onClick={onOpen} />
        </div>
    );
}

function Column({ col, leads, onOpen }) {
    const totalValue = leads.reduce((s, l) => s + (l.value || 0), 0);
    return (
        <div
            data-testid={PIPELINE.column}
            data-column-id={col.id}
            className={`flex flex-col w-72 shrink-0 border-t-4 ${col.accent} bg-secondary/30 rounded-md`}
        >
            <div className="px-3 py-3 flex items-center justify-between">
                <div>
                    <div className="text-[11px] font-semibold uppercase tracking-[0.2em]">{col.label}</div>
                    <div className="text-[10px] text-muted-foreground font-mono mt-0.5">${totalValue.toLocaleString()}</div>
                </div>
                <span className="text-xs font-mono px-2 h-6 inline-flex items-center bg-background border border-border rounded">{leads.length}</span>
            </div>
            <SortableContext items={leads.map((l) => l.id)} strategy={verticalListSortingStrategy}>
                <div className="kanban-column-scroll flex-1 px-2 pb-2 space-y-2 overflow-y-auto min-h-[200px]">
                    {leads.map((l) => <SortableCard key={l.id} lead={l} onOpen={() => onOpen?.(l)} />)}
                    {leads.length === 0 && <div className="text-xs text-muted-foreground text-center py-6 border border-dashed border-border rounded-md">Drop here</div>}
                </div>
            </SortableContext>
        </div>
    );
}

export default function Pipeline() {
    const [leadsByCol, setLeadsByCol] = useState(() => Object.fromEntries(COLUMNS.map((c) => [c.id, []])));
    const [activeLead, setActiveLead] = useState(null);
    const [detailLead, setDetailLead] = useState(null);
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

    const load = useCallback(async () => {
        const { data } = await api.get("/leads?limit=200");
        const grouped = Object.fromEntries(COLUMNS.map((c) => [c.id, []]));
        data.items.forEach((l) => { if (grouped[l.status]) grouped[l.status].push(l); });
        setLeadsByCol(grouped);
    }, []);

    useEffect(() => { load(); }, [load]);

    const onDragStart = (event) => {
        const lead = event.active.data.current?.lead;
        setActiveLead(lead || null);
    };

    const findColumn = (id) => {
        if (leadsByCol[id]) return id;
        for (const [col, arr] of Object.entries(leadsByCol)) {
            if (arr.some((l) => l.id === id)) return col;
        }
        return null;
    };

    const onDragEnd = async (event) => {
        const { active, over } = event;
        setActiveLead(null);
        if (!over) return;
        const fromCol = findColumn(active.id);
        const toCol = findColumn(over.id) || over.id;
        if (!fromCol || !toCol || fromCol === toCol) return;

        // Optimistic move
        const lead = leadsByCol[fromCol].find((l) => l.id === active.id);
        if (!lead) return;
        setLeadsByCol((prev) => ({
            ...prev,
            [fromCol]: prev[fromCol].filter((l) => l.id !== active.id),
            [toCol]: [{ ...lead, status: toCol }, ...prev[toCol]],
        }));
        try {
            await api.patch(`/leads/${active.id}`, { status: toCol });
            toast.success(`Moved to ${COLUMNS.find((c) => c.id === toCol).label}`);
        } catch {
            toast.error("Failed — reverting");
            load();
        }
    };

    return (
        <div className="space-y-6" data-testid={PIPELINE.root}>
            <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-primary">Pipeline</div>
                <h1 className="font-heading text-4xl font-bold tracking-tight mt-1">Sales Board</h1>
                <p className="text-sm text-muted-foreground mt-2">Drag any card between stages to update lead status instantly.</p>
            </div>
            <DndContext sensors={sensors} collisionDetection={closestCorners} onDragStart={onDragStart} onDragEnd={onDragEnd}>
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex gap-4 overflow-x-auto pb-4">
                    {COLUMNS.map((col) => (
                        <Column key={col.id} col={col} leads={leadsByCol[col.id] || []} onOpen={setDetailLead} />
                    ))}
                </motion.div>
                <DragOverlay>
                    {activeLead ? <LeadCard lead={activeLead} dragging onClick={() => setDetailLead(activeLead)} /> : null}
                </DragOverlay>
            </DndContext>

            <AnimatePresence>
                {detailLead && (
                    <LeadQuickView
                        lead={detailLead}
                        onClose={() => setDetailLead(null)}
                    />
                )}
            </AnimatePresence>
        </div>
    );
}

function LeadQuickView({ lead, onClose }) {
    const fields = [
        ["Company", lead.company],
        ["Client", lead.client_name],
        ["Company Email", lead.company_email || "—"],
        ["Client Email", lead.client_email || lead.email || "—"],
        ["Phone", lead.phone || "—"],
        ["Website", lead.website || "—"],
        ["Status", lead.status],
        ["Priority", lead.priority],
        ["Source", lead.source],
        ["Value", `$${Number(lead.value || 0).toLocaleString()}`],
        ["Next Follow-up", lead.next_follow_up ? new Date(lead.next_follow_up).toLocaleString() : "—"],
        ["Notes", lead.notes || "—"],
    ];

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-background/70 backdrop-blur-sm p-4"
            onClick={onClose}
        >
            <motion.aside
                initial={{ x: 32, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: 32, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="ml-auto h-full w-full max-w-xl rounded-md border border-border bg-card shadow-xl overflow-y-auto"
            >
                <div className="sticky top-0 z-10 flex items-start justify-between gap-3 border-b border-border bg-card p-5">
                    <div>
                        <div className="text-[10px] font-semibold uppercase tracking-[0.24em] text-primary">Pipeline Details</div>
                        <h3 className="font-heading text-2xl font-bold tracking-tight mt-1">{lead.company}</h3>
                        <div className="text-sm text-muted-foreground">{lead.client_name}</div>
                    </div>
                    <button onClick={onClose} className="h-8 w-8 inline-flex items-center justify-center rounded hover:bg-secondary" aria-label="Close details">
                        <X className="h-4 w-4" />
                    </button>
                </div>

                <div className="p-5 space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                        <div className="rounded-md border border-border bg-secondary/20 p-3">
                            <div className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">Value</div>
                            <div className="mt-1 font-heading text-2xl font-bold tabular-nums">{`$${Number(lead.value || 0).toLocaleString()}`}</div>
                        </div>
                            <div className={`rounded-md border p-3 ${getStatusToneClasses(lead.priority, { group: "priority", variant: "surface" })}`}>
                                <div className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">Priority</div>
                                <div className={`mt-1 inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold uppercase tracking-wider border ${getStatusToneClasses(lead.priority, { group: "priority", variant: "badge" })}`}>
                                    {lead.priority}
                                </div>
                            </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {fields.map(([label, value]) => (
                            <div key={label} className="rounded-md border border-border p-3">
                                <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">{label}</div>
                                <div className="mt-1 text-sm break-words">{value}</div>
                            </div>
                        ))}
                    </div>

                    <div className="flex items-center justify-end gap-2 pt-2">
                        <button type="button" onClick={onClose} className="h-10 px-4 rounded-md border border-border hover:bg-secondary text-sm inline-flex items-center gap-2">
                            <ChevronLeft className="h-4 w-4" /> Close
                        </button>
                    </div>
                </div>
            </motion.aside>
        </motion.div>
    );
}
